import { pbBaseUrl } from './pbBaseUrl';

export interface ContentPage {
  id: string;
  slug: string;
  title: string;
  subtitle?: string;
  body_md: string;
  meta_description?: string;
  og_image?: string;
  head_inject?: string;
  script_inject?: string;
  published: boolean;
  updated: string;
}

interface PocketBaseListResponse<T> {
  page: number;
  perPage: number;
  totalItems: number;
  items: T[];
}

/**
 * Fetches one published content_pages record by slug. Returns `null` if no
 * match (404, unpublished, or filtered out by access rules).
 *
 * This is the web app's ONLY backend call — an anonymous read of the
 * `content_pages` collection via PocketBase's generic records API, documented in
 * `backend/openapi/openapi.yaml` (tag `collections`) and `EXCEPTIONS.md` §8. It's
 * the single-fetch pattern SK-116 codifies for the RN client; no shared PB SDK.
 *
 * Cached in sessionStorage with a short TTL so repeat navigation is instant.
 */
const TTL_MS = 5 * 60 * 1000;
const STORAGE_PREFIX = 'skf:cp:';

interface CachedEntry {
  storedAt: number;
  data: ContentPage | null;
}

function readCache(slug: string): ContentPage | null | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_PREFIX + slug);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as CachedEntry;
    if (Date.now() - parsed.storedAt > TTL_MS) return undefined;
    return parsed.data;
  } catch {
    return undefined;
  }
}

function writeCache(slug: string, data: ContentPage | null): void {
  if (typeof window === 'undefined') return;
  try {
    const entry: CachedEntry = { storedAt: Date.now(), data };
    window.sessionStorage.setItem(STORAGE_PREFIX + slug, JSON.stringify(entry));
  } catch {
    // Ignore quota / privacy mode
  }
}

export async function fetchContentPage(slug: string): Promise<ContentPage | null> {
  const cached = readCache(slug);
  if (cached !== undefined) return cached;

  const base = pbBaseUrl();
  // The collection's listRule already filters by `published = true`, but
  // include the predicate here too so anonymous fetches stay correct even if
  // the rule is ever loosened.
  const filter = encodeURIComponent(`(slug='${slug.replace(/'/g, '')}' && published=true)`);
  const url = `${base}/api/collections/content_pages/records?perPage=1&filter=${filter}`;

  const res = await fetch(url, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });

  if (!res.ok) {
    writeCache(slug, null);
    return null;
  }

  const json = (await res.json()) as PocketBaseListResponse<ContentPage>;
  const page = json.items?.[0] ?? null;
  writeCache(slug, page);
  return page;
}
